import {MegaScreen} from "./MegaScreen.js";
import {
  STATE_END,
  STATE_INSTRUCTIONS,
  STATE_INTERMISSION,
  STATE_INTRODUCTION,
  STATE_MAIN_COUNTDOWN,
  STATE_MAIN_COUNTDOWN_ENDPAUSE,
  STATE_MAIN_SCOREBOARD,
  STATE_SCENE,
  STATE_SCENE_RESULT, STATE_SCENE_SCOREBOARD,
  STATE_WINNER
} from '@megaphonenpm/mrtsclient/src/constants.js';
import {notNil, isNil} from '@megaphonenpm/mputils/src/utils.js';
import {log, warn} from '@megaphonenpm/mputils/src/logfunctions.js';
import {get} from 'lodash-es';

export class Simulator {
  constructor () {
    this.init();
  }

  autoplay () {
    this.autoplaying = true;
    let actions;
    if (MegaScreen.globals.appState.state === STATE_SCENE) {
      actions = [this.randomizeVotes, this.next, this.next];
    } else {
      actions = [this.next, this.randomizeVotes, this.next];
    }
    let playhead = 0;

    log('Simulator starting autoplay...');
    let nextAction = () => {
      log('Auto-advancing...');
      this.next();
      let currentStateIndex = this.states.indexOf(MegaScreen.globals.appState.state);
      let currentState = this.states[currentStateIndex];
      if (currentState === STATE_SCENE) {
        this.randomizeVotes();
      }
    };
    this.autoplayInterval = setInterval(nextAction, 1100);
    nextAction();
  }

  toggleAutoplay () {
    if (this.autoplaying) {
      this.stopAutoplay();
    } else {
      this.autoplay();
    }
  }

  stopAutoplay () {
    this.autoplaying = false;
    clearInterval(this.autoplayInterval);
  }

  randomizeVotes () {
    MegaScreen.globals.appState.votes = Array(MegaScreen.globals.appState.scene.content.answers.length).fill().map(() => Math.round(Math.random() * 4000));
  }

  init () {
    this.autoplaying = false;
    this.states = [
      STATE_INTRODUCTION,
      STATE_INSTRUCTIONS,
      STATE_MAIN_COUNTDOWN,
      STATE_MAIN_COUNTDOWN_ENDPAUSE,
      STATE_SCENE,
      STATE_SCENE_RESULT,
      STATE_SCENE_SCOREBOARD,
      STATE_END,
      STATE_WINNER,
      STATE_MAIN_SCOREBOARD,
      STATE_INTERMISSION
    ];

    // Configure keyboard shortcuts
    MegaScreen.globals.mousetrap.bind('shift+right', () => this.next());
    MegaScreen.globals.mousetrap.bind('shift+left', () => this.prev());
    MegaScreen.globals.mousetrap.bind('shift+up', () => this.randomizeVotes());
    MegaScreen.globals.mousetrap.bind('shift+a', () => this.toggleAutoplay());
  }

  next () {
    let currentStateIndex = this.states.indexOf(MegaScreen.globals.appState.state);
    let currentState = this.states[currentStateIndex];
    let currentSceneIndex = MegaScreen.globals.playlistService.getSceneIndex(MegaScreen.globals.appState.scene);

    if (currentState === STATE_SCENE_RESULT
        && currentSceneIndex < MegaScreen.globals.dataStore.playlist.scenes.length-1) {
        // Move to next scene in playlist
        MegaScreen.globals.appState.quietSet('scene', MegaScreen.globals.dataStore.playlist.scenes[currentSceneIndex+1]);
        this.seedVotes(currentSceneIndex+1);
        MegaScreen.globals.appState.runstate = STATE_SCENE;
    } else {
      // Move to next state
      let nextStateIndex = currentStateIndex + 1;
      nextStateIndex = nextStateIndex >= this.states.length ? 0 : nextStateIndex;

      if (currentState === STATE_MAIN_COUNTDOWN) {
        // Move to first scene in playlist
        MegaScreen.globals.appState.quietSet('scene', MegaScreen.globals.dataStore.playlist.scenes[0]);
        this.seedVotes(0);
      }

      MegaScreen.globals.appState.runstate = this.states[nextStateIndex];
    }
  }

  prev () {
    let currentStateIndex = this.states.indexOf(MegaScreen.globals.appState.state);
    let currentState = this.states[currentStateIndex];
    let currentSceneIndex = MegaScreen.globals.playlistService.getSceneIndex(MegaScreen.globals.appState.scene);

    if (currentState === STATE_SCENE
        && currentSceneIndex > 0) {
        // Move to previous scene in playlist
        MegaScreen.globals.appState.quietSet('scene', MegaScreen.globals.dataStore.playlist.scenes[currentSceneIndex-1]);
        this.seedVotes(currentSceneIndex-1);
        MegaScreen.globals.appState.runstate = STATE_SCENE;
    } else {
      // Move to previous state
      let prevStateIndex = this.states.indexOf(MegaScreen.globals.appState.state)-1;
      prevStateIndex = prevStateIndex < 0 ? this.states.length-1 : prevStateIndex;

      if (currentState === STATE_END) {
        // Move to last scene in playlist
        MegaScreen.globals.appState.quietSet('sceneID', MegaScreen.globals.dataStore.playlist.scenes[MegaScreen.globals.dataStore.playlist.scenes.length-1].id);
        this.seedVotes(MegaScreen.globals.dataStore.playlist.scenes.length-1);
        // Jump backwards over SCENE_RESULT to SCENE
        MegaScreen.globals.appState.runstate = STATE_SCENE;
      } else {
        MegaScreen.globals.appState.runstate = this.states[prevStateIndex];
      }
    }
  }

  gotoScene (sceneid) {
    let sceneIndex = MegaScreen.globals.playlistService.getSceneIndexForID(sceneid);
    if (sceneIndex >= 0) {
      MegaScreen.globals.appState.quietSet('scene', MegaScreen.globals.dataStore.playlist.scenes[sceneIndex]);
      this.seedVotes(sceneIndex);
      MegaScreen.globals.appState.runstate = STATE_SCENE;
    } else {
      let sceneNotFoundWarning = `[SIMULATOR] gotoScene(): Unable to advance to specified scene: [${sceneid}].`;
      if (sceneIndex === -1) {
        sceneNotFoundWarning += ` Current playlist [name: "${MegaScreen.globals.dataStore.playlist.name}", id: "${MegaScreen.globals.dataStore.playlist.id}"] does not contain scene.`;
      } else if (isNil(sceneIndex)) {
        sceneNotFoundWarning += ' No scenes or no playlist in DataStore.';
      }
      warn(sceneNotFoundWarning)
      MegaScreen.globals.dataStore.logClone();
    }
  }

  gotoState (stateName) {
    if (this.states.includes(stateName)) {
      MegaScreen.globals.appState.runstate = stateName;
    } else {
      warn(`[SIMULATOR] gotoState() aborted. State not found: ${stateName}. State must be one of: `, this.states);
    }
  }

  seedVotes (sceneIndex) {
    // If the scene doesn't have any answers, don't seed the votes
    let scene = MegaScreen.globals.dataStore.playlist.scenes[sceneIndex];

    if (isNil(scene)) {
      warn(`[SIMULATOR] seedVotes(): Scene not found for scene index [${sceneIndex}].`);
    } else {
      let answers = get(scene, 'content.answers');
      if (notNil(answers)) {
        let numAnswers = answers.length;
        MegaScreen.globals.appState.quietSet('votes', Array(numAnswers).fill(0));
      }
    }
  }
}
